import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../../services/api';
import { Invoice, InvoiceStatus, Company } from '../../types/models';
import { format } from 'date-fns';
import { formatCurrency, formatDateOnly } from '../../utils/formatters';

// Helper function to get chip color based on status
const getStatusChipColor = (status: InvoiceStatus | string) => {
  switch (status) {
    case InvoiceStatus.PENDING:
    case 'pending':
      return 'warning';
    case InvoiceStatus.VERIFIED:
    case 'verified':
      return 'info';
    case InvoiceStatus.SOLD:
    case 'sold':
      return 'success';
    case InvoiceStatus.EXPIRED:
    case 'expired':
      return 'error';
    case InvoiceStatus.REJECTED:
    case 'rejected':
      return 'error';
    case InvoiceStatus.DELISTED:
    case 'delisted':
      return 'default';
    default:
      return 'default';
  }
};


const InvoicesList: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    verified: 0,
    sold: 0,
    rejected: 0
  });

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.get<Invoice[]>('/invoices');
        
        // Process invoices to ensure debtor information is available
        const processedInvoices = await Promise.all(data.map(async (invoice) => {
          // If debtor is already included, return as is
          if (invoice.debtor) {
            return invoice;
          }
          
          // Try to fetch debtor by debtor_id if available
          if (invoice.debtor_id) {
            try {
              const debtor = await apiService.get<Company>(`/companies/${invoice.debtor_id}`);
              return { ...invoice, debtor };
            } catch (err) {
              console.error(`Error fetching debtor for invoice ${invoice.id}:`, err);
            }
          }
          
          // Try to fetch company by company_id as fallback
          if (invoice.company_id) {
            try {
              const company = await apiService.get<Company>(`/companies/${invoice.company_id}`);
              return { ...invoice, debtor: company };
            } catch (err) {
              console.error(`Error fetching company for invoice ${invoice.id}:`, err);
            }
          }
          
          return invoice;
        }));
        
        setInvoices(processedInvoices);
        setTotalInvoices(processedInvoices.length);
        
        // Calculate stats
        const statsData = {
          total: processedInvoices.length,
          pending: processedInvoices.filter(inv => inv.status === InvoiceStatus.PENDING).length,
          verified: processedInvoices.filter(inv => inv.status === InvoiceStatus.VERIFIED).length,
          sold: processedInvoices.filter(inv => inv.status === InvoiceStatus.SOLD).length,
          rejected: processedInvoices.filter(inv => inv.status === InvoiceStatus.REJECTED).length
        };
        setStats(statsData);
      } catch (err: any) {
        console.error('Error fetching invoices:', err);
        setError(err.response?.data?.detail || 'Error al cargar las facturas. Por favor intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [refreshKey]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleViewInvoice = (invoiceId: number) => {
    navigate(`/invoices/${invoiceId}`);
  };

  return (
    <Container>
      <Box sx={{ mt: 3, mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Panel de Control
          </Link>
          <Typography color="textPrimary">Mis Facturas</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Mis Facturas
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoices/upload')}
          >
            Subir Nueva Factura
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total de Facturas
              </Typography>
              <Typography variant="h5" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pendientes
              </Typography>
              <Typography variant="h5" component="div" sx={{ color: 'warning.main' }}>
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Verificadas
              </Typography>
              <Typography variant="h5" component="div" sx={{ color: 'info.main' }}>
                {stats.verified}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Vendidas
              </Typography>
              <Typography variant="h5" component="div" sx={{ color: 'success.main' }}>
                {stats.sold}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rechazadas
              </Typography>
              <Typography variant="h5" component="div" sx={{ color: 'error.main' }}>
                {stats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No se encontraron facturas
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              No has subido ninguna factura aún.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/invoices/upload')}
            >
              Sube tu Primera Factura
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Factura #</TableCell>
                    <TableCell>Deudor</TableCell>
                    <TableCell>Fecha de Emisión</TableCell>
                    <TableCell>Fecha de Vencimiento</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>{invoice.invoice_number}</TableCell>
                        <TableCell>
                          {invoice.debtor?.name || 
                           (invoice.company_id ? `ID Empresa: ${invoice.company_id}` : 
                           (invoice.debtor_id ? `ID Deudor: ${invoice.debtor_id}` : 'N/A'))}
                        </TableCell>
                        <TableCell>
                          {formatDateOnly(invoice.issue_date)}
                        </TableCell>
                        <TableCell>
                          {formatDateOnly(invoice.due_date)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status.toUpperCase()}
                            color={getStatusChipColor(invoice.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver Detalles">
                            <IconButton
                              size="small"
                              onClick={() => handleViewInvoice(invoice.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalInvoices}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Container>
  );
};

export default InvoicesList;
